/**
 * AI Observability — Observability Master.
 *
 * One master switch, then one card per type of AI work (reasoning, one-shot,
 * RAG contextual, agents & tools, evals, system). Each card switches that type
 * on or off and, inside it, what its traces keep. A type switched off still
 * records its failures — an error never goes missing.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';

import { PERMISSIONS } from '../../constants/permissions';
import { useHasPermission } from '../../hooks/usePermissions';
import { aiObservabilityService } from '../../services/ai-observability.service';
import {
  OBS_CAPTURE_INFO, OBS_TYPE_INFO,
  type ObsSettings, type ObsSettingsResponse, type ObsType,
} from '../../types/ai-observability.types';
import { ICONS, useObsQuery } from './obs-kit';
import { BackLink, CardHead, ErrorCard } from './obs-shell';

import '../error-analytics/error-analytics.css';
import './ai-observability.css';

/** An on/off control. A real checkbox underneath, so keyboard and screen readers work. */
function Switch({ checked, onChange, disabled, label }: {
  checked: boolean; onChange: (v: boolean) => void; disabled?: boolean; label: string;
}) {
  return (
    <label className={`obs-switch${disabled ? ' off' : ''}`} title={label}>
      <input type="checkbox" checked={checked} disabled={disabled} aria-label={label}
        onChange={(e) => onChange(e.target.checked)} />
      <span />
    </label>
  );
}

/** One type of AI work: observe it or not, and what its traces keep. */
function TypeCard({ type, draft, data, edit, disabled }: {
  type: ObsType; draft: ObsSettings; data: ObsSettingsResponse;
  edit: (fn: (d: ObsSettings) => void) => void; disabled: boolean;
}) {
  const info = OBS_TYPE_INFO[type];
  const rule = draft.types[type];
  const usage = data.usage.byType[type];
  const locked = disabled || !draft.enabled;

  return (
    <section className="ea-card pad ea-c6" style={{ minWidth: 0, opacity: draft.enabled ? 1 : 0.55 }}>
      <CardHead icon={ICONS.layers} title={info.label} sub={info.what}
        right={
          <span style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 'none',
            fontSize: 12.5, fontWeight: 500 }}>
            {rule.observe ? 'Observing' : 'Failures only'}
            <Switch checked={rule.observe} disabled={locked} label={`Observe ${info.label}`}
              onChange={(v) => edit((d) => { d.types[type].observe = v; })} />
          </span>
        } />

      <div className="ea-sub" style={{ fontSize: 12, margin: '2px 0 8px 25px' }}>
        {usage.lastWeek.toLocaleString()} in the last 7 days
        {usage.failed > 0 && ` · ${usage.failed.toLocaleString()} failed all time`}
      </div>

      <div style={{ fontSize: 11.5, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.04em',
        color: 'var(--obs-muted, #64748B)', margin: '10px 0 2px' }}>
        {rule.observe ? 'Capture' : 'Capture (for failures)'}
      </div>
      {data.applies[type].map((c) => (
        <div key={c} style={{ display: 'flex', gap: 12, alignItems: 'center', padding: '9px 0',
          borderTop: '1px solid var(--obs-line-soft)' }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 13, fontWeight: 500 }}>{OBS_CAPTURE_INFO[c].label}</div>
            <div className="ea-sub" style={{ fontSize: 12, marginTop: 2 }}>{OBS_CAPTURE_INFO[c].what}</div>
          </div>
          <Switch checked={rule.capture[c]} disabled={locked}
            label={`${info.label}: capture ${OBS_CAPTURE_INFO[c].label}`}
            onChange={(v) => edit((d) => { d.types[type].capture[c] = v; })} />
        </div>
      ))}
    </section>
  );
}

export default function ObsMaster() {
  const canEdit = useHasPermission(PERMISSIONS.APP_CONFIG_UPDATE);
  const fetchSettings = useCallback(() => aiObservabilityService.getSettings(), []);
  const { data: loaded, loading, error, reload } = useObsQuery(
    fetchSettings, 'Could not load observability settings.');

  // The server's copy and the one being edited, kept apart so "unsaved" is a
  // comparison rather than a flag someone can forget to set.
  const [data, setData] = useState<ObsSettingsResponse | null>(null);
  const [draft, setDraft] = useState<ObsSettings | null>(null);
  const [saving, setSaving] = useState(false);
  const [notice, setNotice] = useState<{ tone: 'ok' | 'bad'; text: string } | null>(null);

  useEffect(() => {
    if (loaded) {
      setData(loaded);
      setDraft(structuredClone(loaded.settings));
    }
  }, [loaded]);

  const dirty = useMemo(
    () => !!data && !!draft && JSON.stringify(data.settings) !== JSON.stringify(draft),
    [data, draft],
  );
  const disabled = !canEdit || saving;

  /** Every edit goes through here: copy, change, replace. */
  const edit = (fn: (d: ObsSettings) => void) => {
    setDraft((d) => {
      if (!d) return d;
      const next = structuredClone(d);
      fn(next);
      return next;
    });
    setNotice(null);
  };

  const save = async () => {
    if (!draft) return;
    setSaving(true);
    setNotice(null);
    try {
      const result = await aiObservabilityService.saveSettings(draft);
      setData(result);
      setDraft(structuredClone(result.settings));
      setNotice({ tone: 'ok', text: 'Saved. AI calls pick this up within 30 seconds.' });
    } catch (e) {
      setNotice({ tone: 'bad', text: e instanceof Error ? e.message : 'Could not save.' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="ea-root obs" style={{ padding: 20, minHeight: '100%' }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between',
        gap: 16, flexWrap: 'wrap', marginBottom: 16 }}>
        <div style={{ minWidth: 0 }}>
          <BackLink to="/ai-observability">AI Observability</BackLink>
          <h1 style={{ margin: '6px 0 0', fontSize: 19, fontWeight: 600, letterSpacing: '-.015em' }}>
            Observability Master
          </h1>
          <div className="ea-sub" style={{ marginTop: 4 }}>
            Choose which AI work is observed, and what each trace keeps.
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flex: 'none' }}>
          {dirty && <span className="obs-chip warn">Unsaved changes</span>}
          <button className="obs-btn" disabled={disabled || !data}
            style={{ padding: '6px 12px' }} title="Everything on (not saved yet)"
            onClick={() => data && setDraft(structuredClone(data.defaults))}>
            Reset to defaults
          </button>
          <button className="obs-btn" disabled={disabled || !dirty}
            onClick={() => data && setDraft(structuredClone(data.settings))}
            style={{ padding: '6px 12px' }}>
            Discard
          </button>
          <button className="obs-btn obs-primary" disabled={disabled || !dirty} onClick={save}
            style={{ padding: '6px 14px' }}>
            {saving ? 'Saving…' : 'Save changes'}
          </button>
        </div>
      </div>

      <ErrorCard error={error} gap />
      {!canEdit && data && (
        <div className="ea-sub" style={{ marginBottom: 14, padding: '9px 12px', borderRadius: 6,
          background: 'var(--obs-sunken)', border: '1px solid var(--obs-line)' }}>
          Read only — changing these needs the App Configuration update permission.
        </div>
      )}
      {notice && (
        <div style={{ marginBottom: 14, padding: '9px 12px', borderRadius: 6, fontSize: 13,
          ...(notice.tone === 'ok'
            ? { background: '#F0FDF4', border: '1px solid #BBF7D0', color: '#166534' }
            : { background: '#FEF2F2', border: '1px solid #FECACA', color: '#991B1B' }) }}>
          {notice.text}
        </div>
      )}

      {loading && !data && <section className="ea-card pad"><div className="obs-empty">Loading…</div></section>}

      {data && draft && (
        <div className="ea-grid">
          <section className="ea-card pad ea-c12" style={{ minWidth: 0 }}>
            <CardHead icon={ICONS.gear} title="Observability"
              sub={`Master switch for all AI tracing · ${data.usage.totalRows.toLocaleString()} traces stored. Switching a type off below still records its failures.`}
              right={
                <span style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 'none',
                  fontSize: 12.5, fontWeight: 500 }}>
                  {draft.enabled ? 'On' : 'Off'}
                  <Switch checked={draft.enabled} disabled={disabled} label="Observability on or off"
                    onChange={(v) => edit((d) => { d.enabled = v; })} />
                </span>
              } />
            {!draft.enabled && (
              <div style={{ marginTop: 8, padding: '9px 12px', borderRadius: 6, fontSize: 12.5,
                background: '#FEF3C7', border: '1px solid #FDE68A', color: '#92400E' }}>
                With observability off, nothing is recorded — not even failures.
              </div>
            )}
          </section>

          {data.types.map((t) => (
            <TypeCard key={t} type={t} draft={draft} data={data} edit={edit} disabled={disabled} />
          ))}
        </div>
      )}

      {data && !loading && (
        <div className="ea-sub" style={{ marginTop: 12, fontSize: 12 }}>
          <button className="obs-btn" onClick={reload} style={{ padding: '4px 10px' }}>Refresh counts</button>
        </div>
      )}
    </div>
  );
}
